<template>
  <div class="page-container">
    <main :class="['main-content', { nested: isMemberCenterContext }]">
      <div class="page-inner-box">

        <div class="error-notes-container">
          <!-- 主内容区 -->
          <div class="content-main">
            <!-- 错题记录区域 -->
            <div class="error-notes-section">
              <div class="section-header">
                <div class="section-title">错题记录</div>
                <div class="section-actions">
                  <el-button type="primary" size="small" @click="goWrongPractice">错题练习</el-button>
                </div>
              </div>

              <!-- 错题卡片列表 -->
              <div v-loading="wrongQuestionsLoading" class="error-notes-grid">
                <div
                  v-for="item in errorNotes"
                  :key="item.id"
                  class="error-note-card"
                  @click="showExplanation(item)"
                >
                  <div class="error-note-header">
                    <div class="error-note-title">{{ item.paperTitle }}</div>
                    <el-tooltip content="确定掌握该题后，该题将从错题库中移除" placement="top">
                      <img 
                        class="error-note-bookmark" 
                        :class="{ mastered: item.isMastered }"
                        :src="item.isMastered ? require('@/assets/image/bookmark-sel.png') : require('@/assets/image/icon_bookmark.png')"
                        @click.stop="handleMarkMastered(item)"
                      />
                    </el-tooltip>
                  </div>

                  <div class="error-note-divider"></div>

                  <div class="error-note-body">
                    <el-tag size="mini" class="question-type">{{ item.type }}</el-tag>
                    <div class="question-stem">{{ item.stem }}</div>
                  </div>
                  
                  <div class="error-note-source" v-if="item.sourceTypeText">
                    错题来源：{{ item.sourceTypeText }}
                  </div>
                </div>
              </div>

              <div class="pagination" v-if="wrongQuestionsTotal > wrongQuestionsQuery.pageSize">
                <el-pagination
                  layout="prev, pager, next"
                  :total="wrongQuestionsTotal"
                  :page-size="wrongQuestionsQuery.pageSize"
                  :current-page="wrongQuestionsQuery.pageNum"
                  prev-text="<"
                  next-text=">"
                  @current-change="handleCurrentChange"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- 错题解析弹窗 -->
    <el-dialog
      :visible.sync="explanationDialogVisible"
      width="800px"
      custom-class="explanation-dialog-v2"
      append-to-body
      :show-close="true"
    >
      <div v-if="currentQuestion" class="explanation-container-v2">
        <!-- 题目正文 -->
        <div class="q-content">
          <div class="q-title">
            <span class="q-index">{{ currentIndex + 1 }}.</span>
            {{ currentQuestion.stem }}
          </div>
          
          <!-- 选项列表 -->
          <div class="q-options-v2">
            <div 
              v-for="opt in currentQuestion.options" 
              :key="opt.letter" 
              class="q-opt-item-v2"
              :class="{
                'is-selected': currentQuestion.userAnswer && currentQuestion.userAnswer.includes(opt.letter),
                'is-correct': currentQuestion.answer && currentQuestion.answer.includes(opt.letter)
              }"
            >
              <div class="opt-circle">{{ opt.letter }}</div>
              <div class="opt-text">{{ opt.content }}</div>
            </div>
          </div>
        </div>

        <!-- 解析卡片 -->
        <div class="analysis-card-v2">
          <div class="status-tag">
            <i class="el-icon-circle-close"></i> 回答错误
          </div>
          <div class="answer-row">
            <span class="label">正确答案:</span>
            <span class="value correct">{{ currentQuestion.answer }}</span>
          </div>
          <div class="answer-row">
            <span class="label">您的答案:</span>
            <span class="value user">{{ currentQuestion.userAnswer || '未作答' }}</span>
          </div>
          <div class="difficulty-row">
            <span class="label">测试难度:</span>
            <el-rate
              v-model="currentQuestion.difficulty"
              disabled
              disabled-void-color="#C6CEde"
              :colors="['#FFB800', '#FFB800', '#FFB800']"
            ></el-rate>
          </div>
          <div class="analysis-row-v2">
            <span class="label">答案解析:</span>
            <span class="value">{{ currentQuestion.explanation || '暂无解析' }}</span>
          </div>
        </div>

        <!-- 底部操作栏 -->
        <div class="dialog-footer-v2">
          <div class="left-actions">
            <div class="action-item" @click="toggleMastery(currentQuestion)">
              <img :src="require('@/assets/image/icon_bookmark.png')" alt="" class="action-icon"> {{ currentQuestion.isMastered ? '已掌握' : '未掌握' }}
            </div>
            <div class="action-item" @click="deleteNote(currentQuestion)">
              <i class="el-icon-delete"></i> 删除
            </div>
          </div>
          <div class="right-buttons">
            <el-button class="nav-btn" :disabled="currentIndex === 0" @click="prevQuestion">上一题</el-button>
            <el-button type="primary" class="nav-btn primary" :disabled="currentIndex === errorNotes.length - 1" @click="nextQuestion">下一题</el-button>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 抽题配置弹窗 -->
    <WrongQuestionPracticeConfigDialog
      :visible.sync="practiceConfigDialogVisible"
      :loading="practiceConfigLoading"
      :max-config="maxConfig"
      :initial-config="practiceConfig"
      @start="startPractice"
    />
  </div>
</template>

<script>
import { getMyWrongQuestions, markWrongQuestionMastered, deleteWrongQuestion, getWrongQuestionPracticeConfig, saveWrongQuestionPracticeConfig, generateWrongQuestionPractice } from '@/api/examApi'
import WrongQuestionPracticeConfigDialog from '@/components/WrongQuestionPracticeConfigDialog.vue'

export default {
  name: 'ErrorNotes',
  components: {
    WrongQuestionPracticeConfigDialog
  },
  computed: {
    isMemberCenterContext() {
      return this.$route.path.startsWith('/member-center')
    },
    totalPracticeCount() {
      let total = 0
      if (this.practiceConfig.enableSingle) total += Number(this.practiceConfig.singleChoiceCount || 0)
      if (this.practiceConfig.enableMultiple) total += Number(this.practiceConfig.multipleChoiceCount || 0)
      if (this.practiceConfig.enableTrueFalse) total += Number(this.practiceConfig.trueFalseCount || 0)
      if (this.practiceConfig.enableEssay) total += Number(this.practiceConfig.essayCount || 0)
      return total
    }
  },
  created() {
    this.loadWrongQuestions()
  },
  data() {
    return {
      wrongQuestionsLoading: false,
      wrongQuestionsTotal: 0,
      wrongQuestionsQuery: {
        courseId: undefined,
        pageNum: 1,
        pageSize: 10
      },
      errorNotes: [],
      explanationDialogVisible: false,
      currentQuestion: null,
      currentIndex: -1,
      // 抽题配置相关
      practiceConfigDialogVisible: false,
      practiceConfigLoading: false,
      maxConfig: {
        singleChoiceCount: 0,
        multipleChoiceCount: 0,
        trueFalseCount: 0,
        essayCount: 0
      },
      practiceConfig: {
        enableSingle: true,
        singleChoiceCount: 10,
        enableMultiple: false,
        multipleChoiceCount: 0,
        enableTrueFalse: false,
        trueFalseCount: 0,
        enableEssay: false,
        essayCount: 0
      }
    }
  },
  methods: {
    extractListAndTotal(data) {
      const list =
        (Array.isArray(data) && data) ||
        (data && Array.isArray(data.list) && data.list) ||
        (data && Array.isArray(data.records) && data.records) ||
        (data && Array.isArray(data.items) && data.items) ||
        []

      const total =
        (data && (data.total ?? data.count ?? data.totalCount)) ||
        (typeof data === 'object' && !Array.isArray(data) ? list.length : list.length)

      return { list, total: Number(total) || 0 }
    },
    normalizeWrongQuestion(item, index) {
      const id = (item && (item.id ?? item.wrongId ?? item.questionId ?? item.question_id)) ?? index
      const paperName = item && (item.paperName ?? item.paper_name)
      const courseName = item && (item.courseName ?? item.course_name)
      const courseId = item && (item.courseId ?? item.course_id)
      const sourceType = item && (item.sourceType ?? item.source_type)
      
      const sourceTypeMap = {
        1: '课程',
        2: '考试',
        3: '模拟练习'
      }
      const sourceTypeText = sourceType != null ? (sourceTypeMap[sourceType] || '') : ''
      
      const paperTitle = paperName || courseName || (courseId != null ? `课程 ${courseId}` : '未知来源')

      const qt = item && (item.questionType ?? item.question_type)
      const qtNum = qt == null ? undefined : Number(qt)
      const typeMap = {
        1: '单选题',
        2: '多选题',
        3: '判断题',
        4: '填空题',
        5: '简答题'
      }
      const type = qtNum != null ? (typeMap[qtNum] || `题型${qtNum}`) : ''

      const stem = String((item && (item.questionContent ?? item.stem ?? item.content)) || '')

      let options = []
      const rawOptions = item && (item.questionOptions ?? item.options)
      if (typeof rawOptions === 'string') {
        try {
          options = JSON.parse(rawOptions)
        } catch (e) {
          options = []
        }
      } else if (Array.isArray(rawOptions)) {
        options = rawOptions
      }

      const answer = String((item && (item.correctAnswer ?? item.answer)) || '')
      const userAnswer = String((item && (item.studentAnswer ?? item.userAnswer ?? item.myAnswer)) || '')
      const explanation = String((item && (item.questionAnalysis ?? item.analysis ?? item.explanation)) || '')
      const difficulty = Number((item && (item.difficulty ?? item.questionDifficulty)) || 3)

      const isMasteredRaw = item && (item.isMastered ?? item.mastered ?? item.is_mastered ?? item.masterStatus ?? item.master_status)
      const isMastered = isMasteredRaw === true || isMasteredRaw === 1 || isMasteredRaw === '1'

      return {
        id,
        paperTitle,
        sourceTypeText,
        type,
        stem,
        options,
        answer,
        userAnswer,
        explanation,
        difficulty,
        isBookmarked: false,
        isMastered,
        raw: item
      }
    },
    showExplanation(item) {
      this.currentQuestion = item
      this.currentIndex = this.errorNotes.findIndex(q => q.id === item.id)
      this.explanationDialogVisible = true
    },
    prevQuestion() {
      if (this.currentIndex > 0) {
        this.currentIndex--
        this.currentQuestion = this.errorNotes[this.currentIndex]
      }
    },
    nextQuestion() {
      if (this.currentIndex < this.errorNotes.length - 1) {
        this.currentIndex++
        this.currentQuestion = this.errorNotes[this.currentIndex]
      }
    },
    async toggleMastery(item) {
      try {
        await markWrongQuestionMastered(item.id)
        item.isMastered = true
        this.$message.success('已标记为掌握')
        await this.loadWrongQuestions()
        if (this.explanationDialogVisible && this.currentQuestion) {
          const updatedIndex = this.errorNotes.findIndex(q => q.id === item.id)
          if (updatedIndex !== -1) {
            this.currentIndex = updatedIndex
            this.currentQuestion = this.errorNotes[updatedIndex]
          } else if (this.errorNotes.length === 0) {
            this.explanationDialogVisible = false
            this.currentQuestion = null
            this.currentIndex = -1
          } else {
            const nextIndex = Math.min(this.currentIndex, this.errorNotes.length - 1)
            this.currentIndex = nextIndex
            this.currentQuestion = this.errorNotes[nextIndex]
          }
        }
      } catch (err) {
        this.$message.error(err.message || '标记失败')
      }
    },
    deleteNote(item) {
      this.$confirm('确定要删除这条错题记录吗？', '提示', {
        type: 'warning'
      }).then(async () => {
        try {
          await deleteWrongQuestion(item.id)
          const index = this.errorNotes.findIndex(q => q.id === item.id)
          if (index !== -1) {
            this.errorNotes.splice(index, 1)
            this.wrongQuestionsTotal--
            if (this.explanationDialogVisible) {
              if (this.errorNotes.length === 0) {
                this.explanationDialogVisible = false
              } else {
                // 自动切换到下一题或上一题
                if (this.currentIndex >= this.errorNotes.length) {
                  this.currentIndex = this.errorNotes.length - 1
                }
                this.currentQuestion = this.errorNotes[this.currentIndex]
              }
            }
            this.$message.success('删除成功')
          }
        } catch (err) {
          this.$message.error(err.message || '删除失败')
        }
      }).catch(() => {})
    },
    async loadWrongQuestions() {
      if (this.wrongQuestionsLoading) return
      this.wrongQuestionsLoading = true
      try {
        const res = await getMyWrongQuestions(this.wrongQuestionsQuery)
        const data = res && res.data
        const { list, total } = this.extractListAndTotal(data)
        this.errorNotes = list.map((it, idx) => this.normalizeWrongQuestion(it, idx))
        this.wrongQuestionsTotal = total
      } catch (err) {
        this.errorNotes = []
        this.wrongQuestionsTotal = 0
        this.$message.error((err && err.message) || '获取错题记录失败')
      } finally {
        this.wrongQuestionsLoading = false
      }
    },
    handleCurrentChange(val) {
      this.wrongQuestionsQuery.pageNum = val
      this.loadWrongQuestions()
    },
    toggleBookmark(item) {
      item.isBookmarked = !item.isBookmarked
    },
    async handleMarkMastered(item) {
      if (item.isMastered) {
        return
      }
      try {
        await this.$confirm('确定掌握该题后，该题将从错题库中移除', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        await markWrongQuestionMastered(item.id)
        this.$message.success('已标记为掌握')
        const index = this.errorNotes.findIndex(q => q.id === item.id)
        if (index !== -1) {
          this.errorNotes.splice(index, 1)
          this.wrongQuestionsTotal--
        }
      } catch (err) {
        if (err && err !== 'cancel' && err !== 'close') {
          this.$message.error((err && err.message) || '标记失败')
        }
      }
    },
    goWrongPractice() {
      this.practiceConfigLoading = true
      getWrongQuestionPracticeConfig().then(res => {
        const data = res.data || {}
        this.maxConfig = {
          singleChoiceCount: Number(data.singleChoiceCount) || 0,
          multipleChoiceCount: Number(data.multipleChoiceCount) || 0,
          trueFalseCount: Number(data.trueFalseCount) || 0,
          essayCount: Number(data.essayCount) || 0
        }
        // 初始化配置，默认选中单选题（如果有）
        this.practiceConfig = {
          enableSingle: this.maxConfig.singleChoiceCount > 0,
          singleChoiceCount: this.maxConfig.singleChoiceCount > 0 ? Math.min(10, this.maxConfig.singleChoiceCount) : 0,
          enableMultiple: false,
          multipleChoiceCount: 0,
          enableTrueFalse: false,
          trueFalseCount: 0,
          enableEssay: false,
          essayCount: 0
        }
        this.practiceConfigDialogVisible = true
      }).catch(err => {
        this.$message.error(err.message || '获取练习配置失败')
      }).finally(() => {
        this.practiceConfigLoading = false
      })
    },
    async startPractice(configPayload) {
      const config = configPayload || this.practiceConfig
      
      let total = 0
      if (config.enableSingle) total += Number(config.singleChoiceCount || 0)
      if (config.enableMultiple) total += Number(config.multipleChoiceCount || 0)
      if (config.enableTrueFalse) total += Number(config.trueFalseCount || 0)
      if (config.enableEssay) total += Number(config.essayCount || 0)

      if (total === 0) {
        this.$message.warning('请至少选择一种题型并设置题目数量')
        return
      }
      
      const apiConfig = {
        singleChoiceCount: config.enableSingle ? config.singleChoiceCount : 0,
        multipleChoiceCount: config.enableMultiple ? config.multipleChoiceCount : 0,
        trueFalseCount: config.enableTrueFalse ? config.trueFalseCount : 0,
        essayCount: config.enableEssay ? config.essayCount : 0
      }
      
      this.practiceConfigLoading = true
      try {
        // 保存配置
        await saveWrongQuestionPracticeConfig(apiConfig)
        // 生成试卷
        const res = await generateWrongQuestionPractice(apiConfig)
        const paperData = res.data

        // 只要有数据且包含题目列表，就认为成功
        // 错题练习可能不返回 paperId，使用临时 ID 或 0
        if (paperData && paperData.questions && paperData.questions.length > 0) {
          const paperId = paperData.paperId || 'temp_' + Date.now()
          
          this.practiceConfigDialogVisible = false
          // 跳转到练习详情页，带上 paperId 和来源标记，同时传递试卷数据避免重复请求
          this.$router.push({
            name: 'practiceDetail',
            params: { 
              id: paperId,
              paperData: paperData
            },
            query: { source: 'wrong_practice' }
          })
        } else {
          console.error('生成试卷返回数据异常:', res)
          this.$message.error('生成试卷失败: 未返回有效的试卷数据')
        }
      } catch (err) {
        this.$message.error(err.message || '开始练习失败')
      } finally {
        this.practiceConfigLoading = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.page-container { min-height: 100vh; display: flex; flex-direction: column; }
.main-content { flex: 1; padding-top: 20px; }
.main-content.nested { margin-top: 0; }
.main-content.nested .page-inner-box { width: 100%; margin: 0; }

.error-notes-container {
  margin-top: 20px;
}

.content-main {
  flex: 1;
}

/* 头部 */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #5c4cf8;
}

.section-actions .el-button {
  width: 80px;
  height: 26px;
  padding: 0;
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 错题卡片网格 */
.error-notes-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  justify-items: center;
}

.error-note-card {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #f2f2f2;
  box-shadow: none;
  width: 90%;
  max-width: 380px;
  padding: 20px 24px;
  box-sizing: border-box;
  transition: all 0.3s;
  cursor: pointer;
}

.error-note-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border-color: #5c4cf8;
}

/* 弹窗样式重构 (UI设计图版本) */
.explanation-dialog-v2 ::v-deep .el-dialog {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1);
}

.explanation-dialog-v2 ::v-deep .el-dialog__header {
  padding: 0;
}

.explanation-dialog-v2 ::v-deep .el-dialog__body {
  padding: 0;
  background-color: #f5f8ff;
}

.explanation-dialog-v2 ::v-deep .el-dialog__headerbtn {
  top: 15px;
  right: 15px;
  z-index: 10;
  width: 32px;
  height: 32px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.explanation-dialog-v2 ::v-deep .el-dialog__headerbtn .el-dialog__close {
  color: #999;
  font-weight: bold;
}

.explanation-container-v2 {
  padding: 40px;
  min-height: 500px;
  display: flex;
  flex-direction: column;
}

/* 题目内容 */
.q-content {
  margin-bottom: 30px;
}

.q-title {
  font-size: 18px;
  color: #333;
  line-height: 1.6;
  margin-bottom: 24px;
}

.q-index {
  margin-right: 8px;
}

.q-options-v2 {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.q-opt-item-v2 {
  display: flex;
  align-items: center;
  font-size: 16px;
  color: #666;
}

.opt-circle {
  width: 28px;
  height: 28px;
  border: 1.5px solid #dcdfe6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-size: 14px;
  color: #999;
  transition: all 0.2s;
}

.q-opt-item-v2.is-selected .opt-circle {
  border-color: #5c4cf8;
  color: #5c4cf8;
}

.q-opt-item-v2.is-correct .opt-circle {
  background-color: #5c4cf8;
  border-color: #5c4cf8;
  color: #fff;
}

/* 解析卡片 */
.analysis-card-v2 {
  background-color: #eef3ff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 40px;
  flex: 1;
}

.status-tag {
  display: flex;
  align-items: center;
  color: #ff5b5b;
  font-size: 16px;
  margin-bottom: 16px;
}

.status-tag i {
  font-size: 20px;
  margin-right: 8px;
}

.answer-row {
  margin-bottom: 12px;
  font-size: 15px;
}

.answer-row .label {
  color: #666;
  margin-right: 8px;
}

.answer-row .value.correct {
  color: #1fc38e;
  font-weight: 500;
}

.answer-row .value.user {
  color: #5c4cf8;
  font-weight: 500;
}

.difficulty-row {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  font-size: 15px;
}

.difficulty-row .label {
  color: #666;
  margin-right: 8px;
}

.analysis-row-v2 {
  display: flex;
  align-items: flex-start;
  font-size: 15px;
  line-height: 1.6;
}

.analysis-row-v2 .label {
  color: #666;
  margin-right: 8px;
  white-space: nowrap;
}

.analysis-row-v2 .value {
  color: #333;
}

/* 底部操作 */
.dialog-footer-v2 {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.left-actions {
  display: flex;
  gap: 24px;
}

.action-item {
  display: flex;
  align-items: center;
  color: #666;
  font-size: 15px;
  cursor: pointer;
  transition: color 0.2s;
}

.action-item:hover {
  color: #5c4cf8;
}

.action-item i,
.action-item .action-icon {
  font-size: 18px;
  margin-right: 6px;
}

.action-item .action-icon {
  width: 18px;
  height: 18px;
  display: inline-block;
}

.right-buttons {
  display: flex;
  gap: 16px;
}

.nav-btn {
  width: 120px;
  height: 44px;
  border-radius: 8px;
  font-size: 16px;
}

.nav-btn.primary {
  background-color: #3e2ae6;
  border-color: #3e2ae6;
}

.nav-btn.primary:hover {
  background-color: #5c4cf8;
  border-color: #5c4cf8;
}

.pagination {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.error-note-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.error-note-title {
  font-size: 15px;
  color: #555;
  line-height: 1.5;
  flex: 1;
  margin-right: 16px;
  font-weight: normal;
}

.error-note-source {
  margin-top: 12px;
  font-size: 12px;
  color: #999;
  text-align: right;
}

.error-note-bookmark {
  width: 20px;
  height: 20px;
  cursor: pointer;
  margin-top: -2px;
  transition: transform 0.2s;
  
  &:hover {
    transform: scale(1.2);
  }
  
  &.mastered {
    opacity: 0.8;
  }
}

.error-note-divider {
  border-bottom: 1px dashed #e8e8e8;
  margin: 16px 0;
}

.error-note-body {
  display: flex;
  align-items: center;
}

.question-type {
  border: none;
  border-radius: 4px;
  padding: 0 8px;
  height: 20px;
  line-height: 20px;
  font-size: 12px;
  background-color: #ebebff;
  color: #5c4cf8;
  margin-right: 12px;
  font-weight: bold;
}

.question-stem {
  flex: 1;
  font-size: 14px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 抽题配置弹窗 */
/* 
.practice-config-dialog ::v-deep .el-dialog {
  border-radius: 12px;
}

.practice-config-dialog ::v-deep .el-dialog__header {
  padding: 20px 0;
  text-align: center;
}

.practice-config-dialog ::v-deep .el-dialog__title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.practice-config-content {
  padding: 0 20px 20px;
}

.config-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.config-item .el-checkbox {
  margin-right: 0;
  font-size: 16px;
  color: #333;
}

.input-wrapper {
  display: flex;
  align-items: center;
}

.input-wrapper .unit {
  margin-left: 8px;
  color: #666;
}

.total-count {
  text-align: center;
  margin: 30px 0;
  font-size: 16px;
  color: #666;
}

.count-number {
  color: #5c4cf8;
  font-weight: bold;
  font-size: 18px;
  margin: 0 4px;
}

.start-practice-btn {
  width: 100%;
  height: 44px;
  font-size: 16px;
  border-radius: 8px;
  background-color: #3e2ae6;
  border-color: #3e2ae6;
}

.start-practice-btn:hover {
  background-color: #5c4cf8;
  border-color: #5c4cf8;
} 
*/

@media (max-width: 768px) {
  .error-notes-grid {
    grid-template-columns: 1fr;
  }

  .error-note-card {
    width: 100%;
    max-width: none;
  }
}
</style>
